import { Button } from '@latitude/button';
import type { CustomClassNames } from 'components/ComponentsTypes';
import { isSameMonth } from 'date-fns/isSameMonth';
import { isSameYear } from 'date-fns/isSameYear';
import { setMonth } from 'date-fns/setMonth';
import { startOfMonth } from 'date-fns/startOfMonth';
import { useDatepickerContext } from 'hooks/useDatepickerContext';
import { useDayPicker, useNavigation } from 'react-day-picker';
import type { ModeValue } from 'utils/dateUtils';

const isSameMonthAndYear = (
  date1: Date | undefined,
  date2: Date | undefined,
): boolean =>
  (date1 && date2 && isSameMonth(date1, date2) && isSameYear(date1, date2)) ||
  false;

const isSelectedMonth = (month: Date, selection: ModeValue): boolean =>
  selection.mode === 'range'
    ? isSameMonthAndYear(month, selection.value?.from) ||
      isSameMonthAndYear(month, selection.value?.to)
    : isSameMonthAndYear(month, selection.value);

/** Render the dropdown to navigate between months. */
export function MonthPicker() {
  const {
    fromDate,
    toDate,
    locale,
    formatters: { formatMonthCaption },
    labels: { labelMonthDropdown },
    today,
    ...daypicker
  } = useDayPicker();

  const classNames = daypicker.classNames as CustomClassNames;

  const { setChangingMonth, selection } = useDatepickerContext();
  const { goToMonth, currentMonth } = useNavigation();

  // Dropdown should appear only when both from/toDate is set
  if (!fromDate) {
    return <></>;
  }
  if (!toDate) {
    return <></>;
  }

  const dropdownMonths: Date[] = [];

  const date = startOfMonth(new Date(currentMonth.getFullYear(), 0, 1));
  for (let month = 0; month <= 11; month++) {
    dropdownMonths.push(setMonth(date, month));
  }

  const handleChange = (selectedMonth: number) => {
    const newMonth = setMonth(startOfMonth(currentMonth), selectedMonth);
    goToMonth(newMonth);
    setChangingMonth(false);
  };

  return (
    <div
      role="listbox"
      aria-label={labelMonthDropdown()}
      className={`${classNames.month_picker_container}`}
    >
      {dropdownMonths.map((month) => {
        const isCurrentMonth = isSameMonth(month, today);
        const currentMonthSelected = isSelectedMonth(month, selection);

        const {
          selectedYear,
          selectedMonth,
          minYear,
          minMonth,
          maxYear,
          maxMonth,
        } = {
          selectedYear: month.getFullYear(),
          selectedMonth: month.getMonth(),
          minYear: fromDate.getFullYear(),
          minMonth: fromDate.getMonth(),
          maxYear: toDate.getFullYear(),
          maxMonth: toDate.getMonth(),
        };

        let isDisabled = false;

        if (selectedYear < minYear) {
          isDisabled = true;
        }
        if (selectedYear > maxYear) {
          isDisabled = true;
        }
        if (selectedYear >= minYear && selectedYear <= maxYear) {
          if (selectedYear === minYear && selectedMonth < minMonth) {
            isDisabled = true;
          }
          if (selectedYear === maxYear && selectedMonth > maxMonth) {
            isDisabled = true;
          }
        }

        // x |   - selected month
        //   | x - current month
        // O | O ---> variant: strong , type: ghost
        // O | x ---> variant: primary, type: ghost
        // x | O ---> variant: primary, type: filled
        // x | x ---> variant: primary, type: filled
        return (
          <div
            key={month.getMonth()}
            role="option"
            aria-selected={currentMonthSelected}
            className={`${classNames.month_picker_row}`}
          >
            <Button
              onClick={() => {
                !isDisabled && handleChange(month.getMonth());
              }}
              htmlType="button"
              disabled={isDisabled}
              type={currentMonthSelected ? 'filled' : 'ghost'}
              size="small"
              variant={
                isCurrentMonth || currentMonthSelected ? 'primary' : 'strong'
              }
              className={`${classNames.month_picker_row_item}`}
              aria-label={`Select ${formatMonthCaption(month, { locale })}`}
            >
              {formatMonthCaption(month, { locale })}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
